import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '../../../components/ui/Icon';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button2';
import StatusPill from '../../../components/ui/StatusPill/StatusPill';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from '../../../components/ui/Empty';
import { TypographyH1 } from '../../../components/ui/Typography';
import { notify } from '../../../services/notificationBus';
import CoverageBanner from './CoverageBanner';
import GapsTable from './GapsTable';
import GapDetailModal from './GapDetailModal';
import { PageLayout } from '../../../components/layout';
import styles from './DocumentAssessment.module.css';

/**
 * One assessment: coverage, readiness, and the gap list.
 *
 * Order on screen is deliberate — coverage first, then readiness, then gaps.
 * The customer must know how much of their document set was actually read
 * before they read anything we claim about it.
 */
/** Long enough not to hammer the API, short enough to feel live. */
const POLL_INTERVAL_MS = 4000;

const AssessmentDetail = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollFailuresRef = useRef(0);

  /**
   * Fetch the assessment.
   *
   * `silent` is what separates a poll from a page load. Toggling `loading` on
   * every poll tore the page down to a skeleton and rebuilt it every few
   * seconds, which reads as the page refreshing itself. A poll updates the data
   * in place and touches nothing else.
   */
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const api = await import('../api');
      const next = await api.getAssessment(assessmentId);
      setAssessment(next);
      pollFailuresRef.current = 0;
      if (silent) setError(null);
      return next;
    } catch (err) {
      if (!silent) {
        setError(err?.message || 'Could not load this assessment');
        return null;
      }
      // A blip while polling must not replace a good page with an error.
      // Only give up after several consecutive failures.
      pollFailuresRef.current += 1;
      if (pollFailuresRef.current >= 3) {
        setError('Lost contact with the server while the assessment was running.');
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  // Extraction takes minutes, so create returns immediately with status RUNNING
  // and the result arrives here. Poll silently until it settles; the backend
  // marks an abandoned run failed, so this cannot spin for ever.
  const status = assessment?.status;
  useEffect(() => {
    if (status !== 'running' && status !== 'pending') return undefined;

    let timer = null;
    const tick = () => load({ silent: true });

    const start = () => {
      if (timer) return;
      timer = setInterval(tick, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    // A minutes-long job in a hidden tab does not need polling; fetch once on
    // return instead so the user sees current state without the wasted calls.
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        tick();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [status, load]);

  const rerun = async () => {
    setBusy(true);
    try {
      const api = await import('../api');
      setAssessment(await api.rerunAssessment(assessmentId));
      pollFailuresRef.current = 0;
      notify({ type: 'success', message: 'Assessment re-run against the latest documents' });
    } catch (err) {
      notify({ type: 'error', title: 'Re-run failed', message: err?.message || 'Unknown error' });
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async (gap) => {
    const rationale = window.prompt(
      `Why is this not a gap?\n\n${gap.title}\n\nA rationale is required — an assessor will ask.`
    );
    if (!rationale || !rationale.trim()) return;
    try {
      const api = await import('../api');
      await api.setGapStatus(assessmentId, gap.gap_id, 'dismissed', rationale.trim());
      await load({ silent: true });
      notify({ type: 'success', message: 'Gap dismissed with rationale' });
    } catch (err) {
      notify({ type: 'error', title: 'Could not dismiss', message: err?.message || '' });
    }
  };

  const reopen = async (gap) => {
    try {
      const api = await import('../api');
      await api.setGapStatus(assessmentId, gap.gap_id, 'open');
      await load({ silent: true });
    } catch (err) {
      notify({ type: 'error', title: 'Could not reopen', message: err?.message || '' });
    }
  };

  const assign = async (gap) => {
    const who = window.prompt(`Assign "${gap.title}" to (email):`);
    if (!who || !who.trim()) return;
    try {
      const api = await import('../api');
      await api.assignGap(assessmentId, gap.gap_id, who.trim());
      await load({ silent: true });
      notify({ type: 'success', message: `Assigned to ${who.trim()}` });
    } catch (err) {
      notify({ type: 'error', title: 'Could not assign', message: err?.message || '' });
    }
  };

  if (loading) {
    return (
      <div className={styles.page} aria-hidden="true">
        <Skeleton className={styles.headerSkeleton} />
        <Skeleton className={styles.headerSkeleton} />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton className={styles.rowSkeleton} key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <Empty role="alert">
          <EmptyHeader>
            <EmptyMedia variant="icon"><Icon name="error" /></EmptyMedia>
            <EmptyTitle>Could not load this assessment</EmptyTitle>
            <EmptyDescription>{error}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }
  if (!assessment) return null;

  const summary = assessment.summary || {};
  const running = assessment.status === 'running' || assessment.status === 'pending';
  const failed = assessment.status === 'failed';

  return (
    <PageLayout
      activeNavItem="document-assessment"
      currentPage={assessment.name || 'Assessment'}
      breadcrumbItems={[{ label: 'Document Assessment', path: '/athena/document-assessment' }, { label: assessment.name || 'Assessment' }]}
      contentClassName={styles.contentAreaFullWidth}
      disableScroll
    >
    <div className={styles.page}>
      <header className={styles.detailHeader}>
        <div className={styles.headerText}>
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate('/athena/document-assessment')}
            startIcon={<Icon name="chevron-left" size="xs" />}
          >
            All assessments
          </Button>
          <TypographyH1>{assessment.name || 'Assessment'}</TypographyH1>
        </div>
        <Button variant="outline" onClick={rerun} loading={busy}>
          {busy ? 'Re-running…' : 'Re-run assessment'}
        </Button>
      </header>

      {running && (
        <div className={styles.runningBanner} role="status" aria-live="polite">
          <Spinner size="sm" />
          <div>
            <strong>Reading your documents…</strong>
            <div className={styles.coverageDetail}>
              {(assessment.document_ids || []).length} document(s). This takes a
              few minutes for a large TARA — you can leave this page and come
              back; the assessment keeps running.
            </div>
          </div>
        </div>
      )}

      {failed && (
        <div className={styles.coverageWarn} role="alert">
          <Icon name="warning" size="sm" tone="warning" />
          <div>
            <strong>This assessment did not finish.</strong>
            <div className={styles.coverageDetail}>
              {assessment.error || 'Please try running it again.'}
            </div>
          </div>
        </div>
      )}

      {!running && <CoverageBanner coverage={assessment.coverage} />}

      {!running && <div className={styles.summaryStrip}>
        <div className={styles.readiness}>
          <span className={styles.readinessValue}>{summary.readiness_percentage ?? 0}%</span>
          <span className={styles.readinessLabel}>audit readiness</span>
        </div>
        <div className={styles.severityCounts}>
          <StatusPill tone="error" label={`${summary.high ?? 0} high`} size="sm" />
          <StatusPill tone="warning" label={`${summary.medium ?? 0} medium`} size="sm" />
          <StatusPill tone="neutral" label={`${summary.low ?? 0} low`} size="sm" />
          {summary.resolved > 0 && (
            <StatusPill tone="success" label={`${summary.resolved} resolved`} size="sm" />
          )}
          {summary.dismissed > 0 && (
            <StatusPill tone="neutral" label={`${summary.dismissed} dismissed`} size="sm" />
          )}
        </div>
      </div>}

      {!running && <GapsTable
        gaps={assessment.gaps || []}
        onOpen={setSelected}
        onDismiss={dismiss}
        onReopen={reopen}
        onAssign={assign}
      />}

      {selected && (
        <GapDetailModal gap={selected} onClose={() => setSelected(null)} />
      )}
    </div>
    </PageLayout>
  );
};

export default AssessmentDetail;
